import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'

import { SearchField } from '../../../../../components/SearchField'
import {
  DateBodyTemplate,
  DateFilterTemplate,
  EInvoiceStatusBodyTemplate,
  EInvoiceStatusFilterTemplate,
  PriceBodyTemplate,
  PriceFilterTemplate,
  TestAndSourceBodyTemplate,
  TestAndSourceFilterTemplate,
} from '../../../../../components/tableTemplates'
import { EInvoiceDto } from '../../../../../data/models'
import { EInvoiceMetadata } from '../../../../../data/models/_generatedUblInvoice'
import { useSearchParam } from '../../../../../hooks'
import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import {
  setCurrentTenantConfig,
  useCurrentTenantConfig,
} from '../../../../../utils/tenants'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { Permissions } from '../../../../authorization'
import { useTenantProjectPermissions } from '../../../../authorization/data/hooks'
import { useConfigContext } from '../../../../ui-config/context'
import { convertEInvoiceDates } from '../../../e-invoice-overview/utils'
import { TestAndSourceFilter, testAndSourceFilterService } from '../../../utils'
import { useEInvoiceCockpit } from '../../data'

const STORAGE_KEY = 'e-invoice-cockpit-visible-columns'
const TOGGLEABLE_FIELDS = [
  'Meta',
  'Payload.CompanyCodeMasterData.Common.Company',
  'Payload.SupplierMasterData.Common.Company',
  'Payload.LegalMonetaryTotal.PayableAmount.Value.Value',
  'Payload.IssueDate.Value',
  'Meta.controlInformation.invoiceClassification',
  'Payload.OrderReference.ID.Value.Value',
  'Meta.file.uploadedBy',
  'Meta.file.uploadedAt',
]

export const Table: React.FC = () => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()
  const { navigationColumnHeader, show } = useColumnToggle(
    config.eInvoiceCockpit.eInvoiceCockpit.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
    true,
  )
  const [globalFilterValue, setGlobalFilterValue] = useSearchParam('search')
  const eInvoiceTable = useRef<DataTable<any>>(null)
  const navigate = useNavigate()
  const { tenantId } = useCurrentTenantConfig()

  const { data: eInvoices, isLoading, error } = useEInvoiceCockpit()
  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  const { data: projectPermissions, isLoading: isLoadingPermissions } =
    useTenantProjectPermissions()
  // const lowerCasePermissions = permissions?.map(p => p.name.toLowerCase())

  useEffect(() => {
    initFilters()
  }, [])

  const initFilters = () => {
    setFilters(defaultFilters)
  }

  useEffect(() => {
    let _filters = { ...filters }

    // @ts-ignore
    _filters['global'].value = globalFilterValue

    setFilters(_filters)
  }, [globalFilterValue])

  const clearFilter = () => {
    initFilters()
  }

  const exportCSV = () => {
    eInvoiceTable.current?.exportCSV()
  }

  const headerActions = (
    <>
      <Button
        icon="pi pi-file-export"
        label={t('eInvoiceCockpit.table.export.label')}
        onClick={exportCSV}
        outlined
      />
      <SearchField
        searchValue={globalFilterValue}
        setSearchValue={setGlobalFilterValue}
        placeholder={t('eInvoiceCockpit.table.search.placeholder')}
        width={23}
        classNameInput="surface-50"
      />
    </>
  )

  const companyCodeMasterDataBodyTemplate = (rowData: EInvoiceDto) => {
    if (!rowData?.Payload?.CompanyCodeMasterData) return null

    return (
      <div className="flex flex-column gap-1">
        <span>
          {rowData?.Payload?.CompanyCodeMasterData?.Common?.DisplayId}
        </span>
        <Link
          to={`/master-data-management/company-codes/edit/${rowData?.Payload?.CompanyCodeMasterData?.Id}`}
          className="text-primary no-underline"
        >
          {rowData?.Payload?.CompanyCodeMasterData?.Common?.Company}
        </Link>
      </div>
    )
  }

  const supplierMasterDataBodyTemplate = (rowData: EInvoiceDto) => {
    if (!rowData?.Payload?.SupplierMasterData) return null

    return (
      <div className="flex flex-column gap-1">
        <span>{rowData?.Payload?.SupplierMasterData?.Common?.DisplayId}</span>
        <Link
          to={`/suppliers/${rowData?.Payload?.SupplierMasterData?.Common?.DisplayId}`}
          className="text-primary no-underline"
        >
          {rowData?.Payload?.SupplierMasterData?.Common?.Company}
        </Link>
      </div>
    )
  }

  const legalMonetaryTotalBodyTemplate = (rowData: EInvoiceDto) => {
    return PriceBodyTemplate(
      rowData?.Payload?.LegalMonetaryTotal?.PayableAmount?.Value?.Value,
      {
        currency:
          rowData?.Payload?.LegalMonetaryTotal?.PayableAmount?.CurrencyID
            ?.Value,
      },
    )
  }

  const invoiceClassificationBodyTemplate = (rowData: EInvoiceDto) => {
    const classification =
      rowData?.Meta?.controlInformation?.invoiceClassification
    if (!classification) return null

    return t(
      `eInvoiceCockpit.table.columns.invoiceClassification.options.${classification}`,
    )
  }

  const invoiceClassificationFilterTemplate = (
    options: ColumnFilterElementTemplateOptions,
  ) => {
    if (!options) return null

    const classifications = [
      {
        label: t(
          'eInvoiceCockpit.table.columns.invoiceClassification.options.FI',
        ),
        value: 'FI',
      },
      {
        label: t(
          'eInvoiceCockpit.table.columns.invoiceClassification.options.MM',
        ),
        value: 'MM',
      },
    ]

    return (
      <Dropdown
        value={options.value}
        options={classifications}
        onChange={(e: DropdownChangeEvent) =>
          options.filterCallback(e.value, options.index)
        }
        className="p-column-filter"
        placeholder={t(
          'eInvoiceCockpit.table.columns.invoiceClassification.filterPlaceholder',
        )}
      />
    )
  }

  const actionBodyTemplate = (rowData: EInvoiceDto) => {
    const projectId = rowData?.Recipient

    const invoiceNumber = rowData?.Payload?.ID?.Value?.Value

    const handleAttachmentsClick = () => {
      navigate(`documents/${rowData?.id}`, {
        state: { attachments: rowData?.Attachments },
      })
    }

    const handleGoToEInvoiceClick = () => {
      setCurrentTenantConfig({ tenantId, projectId: projectId })

      navigate(`/e-invoices/${rowData.id}`)
    }

    const projetPermission = projectPermissions?.find(
      projectPermission => projectPermission.project === projectId,
    )
    const lowerCaseProjectPermissions = projetPermission?.permissions.map(
      permission => permission.name.toLowerCase(),
    )
    const hasEInvoicePermission = lowerCaseProjectPermissions?.includes(
      Permissions.Project.EInvoice.toLowerCase(),
    )

    if (!hasEInvoicePermission) return null

    return (
      <div className="flex no-wrap gap-2">
        <Button
          icon="pi pi-paperclip"
          rounded
          text
          aria-label={t('eInvoiceCockpit.table.columns.actions.attachments', {
            invoiceNumber,
          })}
          onClick={handleAttachmentsClick}
        />

        <Button
          icon="pi pi-angle-right text-2xl"
          rounded
          text
          aria-label={t('eInvoiceCockpit.table.columns.actions.goToEInvoice', {
            invoiceNumber,
          })}
          onClick={handleGoToEInvoiceClick}
        />
      </div>
    )
  }

  const buttons = (
    <Column
      key="attachmentButtons"
      body={actionBodyTemplate}
      align="right"
      header={
        <div className="flex justify-content-end">{navigationColumnHeader}</div>
      }
    />
  )

  return (
    <div className="col-12">
      <Outlet />

      <ConfigurableTable
        ref={eInvoiceTable}
        headerTitle={t('eInvoiceCockpit.table.title')}
        config={config.eInvoiceCockpit.eInvoiceCockpit.table}
        data={convertEInvoiceDates(eInvoices)}
        dataKey="id"
        stateKey="table-e-invoices-table"
        headerActions={headerActions}
        defaultFilters={filters}
        updateDefaultFilters={e => setFilters(e as any)}
        clearDefaultFilter={clearFilter}
        globalFilterFields={['Payload.ID.Value.Value']}
        setGlobalFilterValue={setGlobalFilterValue}
        sortField="Payload.IssueDate.Value"
        sortOrder={-1}
        filterDisplay="menu"
        isLoading={isLoading || isLoadingPermissions}
        buttons={buttons}
      >
        {[
          show('Meta') ? (
            <Column
              key="Meta"
              field="Meta"
              header={t('eInvoiceOverview.table.columns.testAndSource.header')}
              body={(rowData: EInvoiceDto) =>
                TestAndSourceBodyTemplate(
                  rowData?.Meta?.controlInformation?.trialFlag,
                  rowData?.Meta?.controlInformation?.processingType,
                )
              }
              filter
              filterElement={TestAndSourceFilterTemplate}
              filterMatchMode={FilterMatchMode.EQUALS}
              showFilterMatchModes={false}
              align="center"
            />
          ) : null,
          show('Payload.CompanyCodeMasterData.Common.Company') ? (
            <Column
              key="CompanyCodeMasterData"
              field="Payload.CompanyCodeMasterData.Common.Company"
              header={t(
                'eInvoiceCockpit.table.columns.companyCodeMasterData.header',
              )}
              body={companyCodeMasterDataBodyTemplate}
              filter
              filterMatchMode={FilterMatchMode.STARTS_WITH}
              filterPlaceholder={t(
                'eInvoiceCockpit.table.columns.companyCodeMasterData.filterPlaceholder',
              )}
              sortable
              className="white-space-nowrap"
            />
          ) : null,
          show('Payload.SupplierMasterData.Common.Company') ? (
            <Column
              key="SupplierMasterData"
              style={{ width: '300px' }}
              field="Payload.SupplierMasterData.Common.Company"
              header={t(
                'eInvoiceCockpit.table.columns.supplierMasterData.header',
              )}
              body={supplierMasterDataBodyTemplate}
              filter
              filterMatchMode={FilterMatchMode.STARTS_WITH}
              filterPlaceholder={t(
                'eInvoiceCockpit.table.columns.supplierMasterData.filterPlaceholder',
              )}
              sortable
              className="white-space-nowrap"
            />
          ) : null,
          show('Payload.LegalMonetaryTotal.PayableAmount.Value.Value') ? (
            <Column
              key="LegalMonetaryTotal"
              field="Payload.LegalMonetaryTotal.PayableAmount.Value.Value"
              header={t(
                'eInvoiceCockpit.table.columns.legalMonetaryTotal.header',
              )}
              body={legalMonetaryTotalBodyTemplate}
              align="right"
              filter
              filterElement={options =>
                PriceFilterTemplate(
                  options,
                  undefined,
                  t(
                    'eInvoiceCockpit.table.columns.legalMonetaryTotal.filterPlaceholder',
                  ),
                )
              }
              filterMatchMode={FilterMatchMode.GREATER_THAN}
              filterPlaceholder={t(
                'eInvoiceCockpit.table.columns.legalMonetaryTotal.filterPlaceholder',
              )}
              sortable
              dataType="numeric"
            />
          ) : null,
          <Column
            key="ID"
            field="Payload.ID.Value.Value"
            header={t('eInvoiceCockpit.table.columns.invoiceNumber.header')}
            filter
            filterMatchMode={FilterMatchMode.STARTS_WITH}
            filterPlaceholder={t(
              'eInvoiceCockpit.table.columns.invoiceNumber.filterPlaceholder',
            )}
            sortable
          />,
          show('Payload.IssueDate.Value') ? (
            <Column
              key="InvoiceDate"
              field="Payload.IssueDate.Value"
              header={t('eInvoiceCockpit.table.columns.invoiceDate.header')}
              body={(rowData: EInvoiceDto) =>
                DateBodyTemplate(rowData.Payload?.IssueDate?.Value ?? undefined)
              }
              filter
              filterElement={DateFilterTemplate}
              filterMatchMode={FilterMatchMode.DATE_IS}
              sortable
            />
          ) : null,
          show('Meta.controlInformation.invoiceClassification') ? (
            <Column
              key="invoiceClassification"
              field="Meta.controlInformation.invoiceClassification"
              header={t(
                'eInvoiceCockpit.table.columns.invoiceClassification.header',
              )}
              body={invoiceClassificationBodyTemplate}
              filter
              filterMatchMode={FilterMatchMode.EQUALS}
              filterElement={invoiceClassificationFilterTemplate}
              sortable
            />
          ) : null,
          show('Payload.OrderReference.ID.Value.Value') ? (
            <Column
              key="OrderReference"
              field="Payload.OrderReference.ID.Value.Value"
              header={t('eInvoiceCockpit.table.columns.orderReference.header')}
              filter
              filterMatchMode={FilterMatchMode.STARTS_WITH}
              sortable
            />
          ) : null,
          show('Meta.file.uploadedBy') ? (
            <Column
              key="CreatedBy"
              field="Meta.file.uploadedBy"
              header={t('eInvoiceOverview.table.columns.createdBy.header')}
              filter
              filterMatchMode={FilterMatchMode.STARTS_WITH}
              filterPlaceholder={t(
                'eInvoiceOverview.table.columns.createdBy.filterPlaceholder',
              )}
              sortable
              className="white-space-nowrap"
            />
          ) : null,
          show('Meta.file.uploadedAt') ? (
            <Column
              key="CreatedAt"
              field="Meta.file.uploadedAt"
              header={t('eInvoiceOverview.table.columns.createdAt.header')}
              body={rowData =>
                DateBodyTemplate(rowData.Meta.file.uploadedAt, undefined, true)
              }
              filter
              filterMatchMode={FilterMatchMode.DATE_IS}
              filterElement={DateFilterTemplate}
              sortable
            />
          ) : null,
          <Column
            key="Status"
            field="Meta.file.status"
            header={t('eInvoiceOverview.table.columns.status.header')}
            body={rowData =>
              EInvoiceStatusBodyTemplate(rowData.Meta.file.status)
            }
            filter
            filterMatchMode={FilterMatchMode.EQUALS}
            filterElement={EInvoiceStatusFilterTemplate}
            filterPlaceholder={t(
              'eInvoiceOverview.table.columns.status.filterPlaceholder',
            )}
            sortable
            frozen
            alignFrozen="right"
          />,
        ].filter((c): c is JSX.Element => c !== null)}
      </ConfigurableTable>
    </div>
  )
}

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  Meta: {
    value: null,
    matchMode: FilterMatchMode.CUSTOM,
  },
  'Payload.CompanyCodeMasterData.Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Payload.SupplierMasterData.Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Payload.LegalMonetaryTotal.PayableAmount.Value.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.GREATER_THAN }],
  },
  'Payload.ID.Value.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Payload.IssueDate.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  'Meta.controlInformation.invoiceClassification': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Payload.OrderReference.ID.Value.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Meta.file.uploadedBy': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Meta.file.uploadedAt': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
  'Meta.file.status': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
}

FilterService.register(
  'custom_Meta',
  (value: EInvoiceMetadata, filters: TestAndSourceFilter) => {
    return testAndSourceFilterService(
      value.controlInformation.trialFlag,
      value.controlInformation.processingType,
      filters,
    )
  },
)
